import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { TFunction } from 'i18next'
import { FC, useState, useEffect, useRef } from 'react'
import { MuiClasses } from '../../../../types'
import { ReserveData, ReservedShowing } from '../../../../types/showings'
import ShowingCard from '../../Overview/ShowingCard'
import showingsService from '../../../../services/showings'
import { useParams } from 'react-router-dom'
import Suggest from '../../../Apartment/Stepper/Steps/Suggest'
import customCloseIcon from '../../../../images/customCloseIcon.svg'

const useStyles = makeStyles(() => ({
  outlinedBtn: {
    backgroundColor: '#FFFFFF',
    color: '#2f2f2f',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    borderRadius: '10px',
    height: '44px',
    '&:hover': {
      backgroundColor: '#f2f2f2',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
    },
    textTransform: 'uppercase'
  }
}))

export interface ReservedCardProps {
  reserveData: ReserveData
  reserved: ReservedShowing
  address?: string
  t: TFunction
  classes: MuiClasses
}

const ReservedCard: FC<ReservedCardProps> = ({
  reserved,
  classes,
  address,
  t
}) => {
  const { applicationCode } = useParams<{ applicationCode: string }>()
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const localClasses = useStyles()
  const modalRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  const handleCancel = async () => {
    setLoading(true)
    try {
      await showingsService.cancelShowingByApplicant({
        applicationCode: applicationCode,
        showingId: reserved.showingId
      })
      const redirectUrl = `/reserve/${applicationCode}/suggest?showingCancelled=true`
      window.location.replace(redirectUrl)
    } catch (error) {
      console.error('Error cancelling reservation:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showModal &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setShowModal(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showModal])

  return (
    <>
      <Grid container justify='center' className={classes.cardWrapper}>
        <CardContent>
          <Grid item sm={12} md={12} className={classes.centerTitle}>
            <Typography
              variant='h5'
              className={classes.titleReserveCard}
              style={{ textAlign: 'center', marginBottom: '1rem' }}
            >
              {t('showings.reserve.reserved')}
            </Typography>
          </Grid>

          <ShowingCard
            showing={reserved}
            t={t}
            useCase='empty'
            address={address}
          />

          <Card className={classes.cardInfo}>
            <Typography variant='body1' style={{ textAlign: 'center' }}>
              {t('showings.reserve.success')}
            </Typography>
          </Card>

          <Grid item style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Button
              disabled={loading}
              variant='contained'
              color='secondary'
              onClick={() => setShowModal(true)}
              fullWidth
            >
              {t('showings.reserve.cancel')}
            </Button>
          </Grid>
        </CardContent>
      </Grid>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300
          }}
        >
          <div
            ref={modalRef}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: isSmallScreen ? '2rem 1rem' : '2.8rem 5rem',
              maxWidth: '600px',
              width: '100%',
              position: 'relative',
              boxSizing: 'border-box'
            }}
          >
            <img
              src={customCloseIcon}
              alt='Close'
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                width: '20px',
                height: '20px',
                cursor: 'pointer'
              }}
            />

            <Typography
              variant='h5'
              align='center'
              className={classes.modalTitle}
              style={{ marginBottom: '1rem' }}
            >
              {t('showings.reserve.cancelDialog.title')}
            </Typography>

            <Suggest
              isModal
              containerMaxWidth='100%'
              contentMaxWidth='100%'
              content={t('showings.reserve.cancelDialog.message')}
            />

            <Grid container spacing={2} style={{ marginTop: '0.8rem' }}>
              <Grid item sm={6} xs={12}>
                <Button
                  fullWidth
                  className={localClasses.outlinedBtn}
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  {t('showings.reserve.cancelDialog.cancel')}
                </Button>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Button
                  variant='contained'
                  fullWidth
                  className={classes.primaryBtn}
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ textTransform: 'uppercase', height: '44px' }}
                >
                  {loading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    t('showings.reserve.cancelDialog.confirm')
                  )}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>
      )}
    </>
  )
}

export default ReservedCard
